import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import { PlanningTicket, PlanningTicketStore, TICKET_STATUSES, Milestone } from 'store/planning';
import { UserStore } from 'store/User';

const STATUS_LABELS: Record<string, string> = {
    todo: 'To Do',
    in_progress: 'In Progress',
    review: 'Review',
    done: 'Done',
    blocked: 'Blocked',
};

interface Props {
    open: boolean;
    ticket: PlanningTicket | null;
    milestone: Milestone | null;
    ticketStore: PlanningTicketStore;
    userStore: UserStore;
    onClose: () => void;
    onSaved: () => void;
}

@observer
export class TicketForm extends Component<Props> {
    @observable title = '';
    @observable description = '';
    @observable estimatedHours = '0';
    @observable status = 'todo';
    @observable assigneeId: number | null = null;
    @observable saving = false;

    componentDidUpdate(prevProps: Props) {
        if (this.props.open && !prevProps.open) {
            this.populateFromTicket();
        }
    }

    @action
    populateFromTicket() {
        const { ticket } = this.props;
        if (ticket) {
            this.title = ticket.title || '';
            this.description = ticket.description || '';
            this.estimatedHours = String(ticket.estimatedHours || 0);
            this.status = ticket.status || 'todo';
            const assignee = ticket.assignee as any;
            this.assigneeId = assignee?.id || assignee?.pk || null;
        } else {
            this.title = '';
            this.description = '';
            this.estimatedHours = '0';
            this.status = 'todo';
            this.assigneeId = null;
        }
    }

    getUserLabel = (u: any): string => {
        if (!u) return '';
        if (u.fullName) return u.fullName;
        const first = u.firstName || '';
        const last = u.lastName || '';
        const name = `${first} ${last}`.trim();
        return name || u.email || String(u.id);
    };

    handleSave = async () => {
        if (!this.props.milestone) return;
        if (!this.title.trim()) return;

        this.saving = true;
        try {
            const { ticket, milestone, ticketStore, onSaved } = this.props;
            const api = (ticketStore as any).api;
            const milestoneId = milestone.id;

            if (ticket) {
                // PATCH existing — send all fields with correct snake_case names.
                // Explicitly send assignee as null when unassigned so the backend
                // clears the field (prevents "editing a user results in an error").
                const payload: Record<string, any> = {
                    title: this.title.trim(),
                    description: this.description,
                    estimated_hours: parseFloat(this.estimatedHours) || 0,
                    status: this.status,
                    milestone: milestoneId,
                    order: ticket.order || 0,
                    assignee: this.assigneeId,
                };
                await api.patch(`planning_ticket/${ticket.id}/`, payload);
            } else {
                // POST new — omit assignee if null to avoid backend null validation issues
                const payload: Record<string, any> = {
                    title: this.title.trim(),
                    description: this.description,
                    estimated_hours: parseFloat(this.estimatedHours) || 0,
                    status: this.status,
                    milestone: milestoneId,
                    order: ticketStore.models.length,
                };
                if (this.assigneeId) {
                    payload.assignee = this.assigneeId;
                }
                await api.post('planning_ticket/', payload);
            }
            await ticketStore.fetch({ with: ['milestone', 'assignee'] });
            onSaved();
        } catch (e) {
            console.error('Failed to save ticket:', e);
        } finally {
            this.saving = false;
        }
    }

    handleDelete = async () => {
        const { ticket, ticketStore, onSaved } = this.props;
        if (!ticket) return;
        if (!window.confirm(`Delete "${ticket.title}"?`)) return;
        this.saving = true;
        try {
            const api = (ticketStore as any).api;
            await api.delete(`planning_ticket/${ticket.id}/`);
            await ticketStore.fetch({ with: ['milestone', 'assignee'] });
            onSaved();
        } catch (e) {
            console.error('Failed to delete ticket:', e);
        } finally {
            this.saving = false;
        }
    }

    render() {
        if (!this.props.open) return null;
        const { ticket, userStore, milestone, onClose } = this.props;
        const isEditing = !!ticket;

        return (
            <div className="sp-modal-overlay" data-test-ticket-form onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
                <div className="sp-modal">
                    <div className="sp-modal-header">
                        <h3>{isEditing ? 'Edit Ticket' : 'New Ticket'}</h3>
                        <button className="sp-btn-icon" onClick={onClose} data-test-ticket-form-close>&times;</button>
                    </div>
                    <div className="sp-modal-body">
                        <div className="sp-form-group">
                            <label>Title</label>
                            <input
                                className="sp-form-input"
                                type="text"
                                value={this.title}
                                data-test-ticket-form-title
                                onChange={(e) => this.title = e.target.value}
                                placeholder="Enter ticket title..."
                            />
                        </div>
                        <div className="sp-form-group">
                            <label>Description</label>
                            <textarea
                                className="sp-form-textarea"
                                value={this.description}
                                data-test-ticket-form-description
                                onChange={(e) => this.description = e.target.value}
                                placeholder="Enter ticket description..."
                            />
                        </div>
                        {milestone && (
                            <div className="sp-form-group">
                                <label>Milestone</label>
                                <div className="sp-form-readonly">
                                    {(milestone as any).name || `Milestone #${milestone.id}`}
                                </div>
                            </div>
                        )}
                        <div className="sp-form-row">
                            <div className="sp-form-group">
                                <label>Estimated Hours</label>
                                <input
                                    className="sp-form-input"
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={this.estimatedHours}
                                    data-test-ticket-form-hours
                                    onChange={(e) => this.estimatedHours = e.target.value}
                                />
                            </div>
                            <div className="sp-form-group">
                                <label>Status</label>
                                <select
                                    className="sp-form-select"
                                    value={this.status}
                                    data-test-ticket-form-status
                                    onChange={(e) => this.status = e.target.value}
                                >
                                    {TICKET_STATUSES.map(s => (
                                        <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="sp-form-group">
                            <label>Assignee</label>
                            <select
                                className="sp-form-select"
                                value={this.assigneeId || ''}
                                data-test-ticket-form-assignee
                                onChange={(e) => this.assigneeId = e.target.value ? parseInt(e.target.value) : null}
                            >
                                <option value="">Unassigned</option>
                                {userStore.models.map((u: any) => (
                                    <option key={u.id} value={u.id}>
                                        {this.getUserLabel(u)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="sp-modal-footer">
                        {isEditing && (
                            <button
                                className="sp-btn sp-btn-danger"
                                style={{ marginRight: 'auto' }}
                                onClick={this.handleDelete}
                                disabled={this.saving}
                                data-test-ticket-form-delete
                            >
                                Delete
                            </button>
                        )}
                        <button className="sp-btn" onClick={onClose} disabled={this.saving}>Cancel</button>
                        <button
                            className="sp-btn sp-btn-primary"
                            onClick={this.handleSave}
                            disabled={this.saving || !this.title.trim()}
                            data-test-ticket-form-save
                        >
                            {this.saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default TicketForm;
